import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBots } from '../context/BotContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const TAB_STYLE = (active) => ({
  padding: '0.55rem 1rem', background: active ? 'rgba(245,158,11,0.12)' : 'transparent',
  border: active ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent',
  borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: active ? 600 : 400,
  color: active ? '#f59e0b' : '#71717a', fontFamily: 'Geist, sans-serif', whiteSpace: 'nowrap', transition: 'all 0.15s',
});

const INPUT = { width: '100%', padding: '0.65rem 0.9rem', background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fafafa', fontSize: '0.875rem', fontFamily: 'Geist, sans-serif', outline: 'none' };
const TEXTAREA = { ...INPUT, minHeight: 100, resize: 'vertical', lineHeight: 1.6 };

const sentimentData = [
  {t:'Mon',score:3.8},{t:'Tue',score:4.1},{t:'Wed',score:3.6},{t:'Thu',score:4.4},
  {t:'Fri',score:4.2},{t:'Sat',score:4.6},{t:'Sun',score:4.3},
];

const TONES = ['friendly and professional','concise and technical','warm and empathetic','formal and corporate','casual and conversational'];
const POSITIONS = ['right','left'];

const PLAYBOOK_TEMPLATES = [
  { trigger: 'sentiment < 3', action: 'Send apology message + 10% discount code', icon: '😔' },
  { trigger: 'keyword: refund', action: 'Escalate to human support immediately', icon: '🔄' },
  { trigger: 'keyword: competitor', action: 'Highlight your unique value propositions', icon: '🏆' },
  { trigger: 'repeat complaint (2+)', action: 'Auto-create support ticket + notify manager', icon: '🎫' },
];

const INTEGRATIONS = [
  { key: 'stripe', label: 'Stripe', icon: '💳', desc: 'Issue refunds & discounts automatically', category: 'Payments' },
  { key: 'shopify', label: 'Shopify', icon: '🛒', desc: 'Order lookup, return processing', category: 'E-commerce' },
  { key: 'hubspot', label: 'HubSpot', icon: '🔶', desc: 'Sync contacts & deal pipeline', category: 'CRM' },
  { key: 'zendesk', label: 'Zendesk', icon: '🎧', desc: 'Auto-create tickets with context', category: 'Support' },
  { key: 'gmail', label: 'Gmail', icon: '📧', desc: 'Send recovery emails automatically', category: 'Email' },
  { key: 'intercom', label: 'Intercom', icon: '💬', desc: 'Sync conversations & user data', category: 'Support' },
];

export default function BotDetail() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { bots, updateBot, regenerateApiKey, addPlaybook, addFile } = useBots();
  const bot = bots.find(b => b.id === botId);
  const [tab, setTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newPlaybook, setNewPlaybook] = useState({ trigger: '', action: '' });
  const [explainLoading, setExplainLoading] = useState(false);
  const [explanation, setExplanation] = useState('');
  const fileInputRef = useRef();

  if (!bot) return (
    <div style={{ minHeight:'100vh', background:'#09090b', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'1rem', fontFamily:'Geist,sans-serif', color:'#71717a' }}>
      <div style={{ fontSize: '2rem' }}>🤖</div>
      <div>Bot not found</div>
      <button onClick={() => navigate('/dashboard')} style={{ background:'#f59e0b', color:'#09090b', border:'none', borderRadius:8, padding:'0.5rem 1.25rem', cursor:'pointer', fontFamily:'Geist,sans-serif', fontWeight:600 }}>← Back to dashboard</button>
    </div>
  );

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000); }, 500);
  };

  const handleUpdate = (field, val) => updateBot(botId, { [field]: val });

  const copyApiKey = () => {
    navigator.clipboard?.writeText(bot.apiKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const copyEmbed = () => {
    const snippet = generateEmbedSnippet(bot);
    navigator.clipboard?.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileData = { name: file.name, size: file.size, type: file.type, uploadedAt: new Date().toISOString(), id: `f_${Date.now()}` };
    addFile(botId, fileData);
    e.target.value = '';
  };

  const handleExplainDrop = () => {
    setExplainLoading(true);
    setExplanation('');
    setTimeout(() => {
      setExplainLoading(false);
      setExplanation('📉 Revenue dropped 12% today primarily due to a spike in shipping delay complaints (38% of conversations). 3 customers mentioned a competitor. Bot escalation rate rose to 18% vs. 9% average — primarily billing questions the knowledge base doesn\'t cover. Recommended action: add shipping ETA to knowledge base and review billing FAQ coverage.');
    }, 1800);
  };

  const tabs = ['overview', 'configure', 'knowledge', 'playbooks', 'integrations', 'embed'];

  return (
    <div style={{ minHeight: '100vh', background: '#09090b', fontFamily: 'Geist, sans-serif', color: '#fafafa' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '1.25rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0d0d10', position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 8, padding: '0.4rem 0.85rem', color: '#a1a1aa', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.8rem' }}>← Dashboard</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>🤖</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{bot.name}</div>
              <div style={{ fontSize: '0.72rem', color: '#71717a' }}>{bot.companyName || 'No company set'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.65rem', background: bot.active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${bot.active ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`, borderRadius: 100, fontSize: '0.72rem', color: bot.active ? '#10b981' : '#ef4444' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
            {bot.active ? 'Active' : 'Inactive'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {saved && <span style={{ fontSize: '0.78rem', color: '#10b981' }}>✓ Saved</span>}
          <button onClick={() => handleUpdate('active', !bot.active)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 1rem', color: '#a1a1aa', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.82rem' }}>
            {bot.active ? 'Pause bot' : 'Activate bot'}
          </button>
          <button onClick={handleSave} disabled={saving} style={{ background: '#f59e0b', color: '#09090b', border: 'none', borderRadius: 8, padding: '0.55rem 1.25rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.85rem', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ padding: '0.75rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '0.4rem', overflowX: 'auto' }}>
        {tabs.map(t => <button key={t} onClick={() => setTab(t)} style={TAB_STYLE(tab === t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
      </div>

      <div style={{ padding: '2rem', maxWidth: 1000 }}>

        {/* ======================== OVERVIEW ======================== */}
        {tab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
              {[
                { label: 'Conversations', value: bot.conversations.toLocaleString(), color: '#fafafa' },
                { label: 'Resolved', value: bot.resolved.toLocaleString(), color: '#10b981' },
                { label: 'Escalated', value: bot.escalated, color: '#f59e0b' },
                { label: 'Revenue', value: `$${bot.revenue.toLocaleString()}`, color: '#3b82f6' },
              ].map(s => (
                <div key={s.label} style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1.25rem' }}>
                  <div style={{ fontSize: '0.72rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: s.color, fontFamily: 'Geist Mono, monospace' }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Sentiment chart */}
            <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Sentiment Score (7 days)</div>
              <div style={{ fontSize: '0.75rem', color: '#71717a', marginBottom: '1.25rem' }}>Average customer satisfaction rating per day</div>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={sentimentData} margin={{top:0,right:0,bottom:0,left:-30}}>
                  <defs>
                    <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[1,5]} tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="score" stroke="#f59e0b" strokeWidth={2} fill="url(#sg)" name="Satisfaction" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Explain the Drop button */}
            <div style={{ background: '#0f0d00', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: explanation || explainLoading ? '1rem' : 0 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>🔍 Explain the Drop</div>
                  <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: 2 }}>AI analyzes your data and explains what's hurting performance</div>
                </div>
                <button onClick={handleExplainDrop} disabled={explainLoading} style={{ background: '#f59e0b', color: '#09090b', border: 'none', borderRadius: 8, padding: '0.55rem 1.25rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.85rem', opacity: explainLoading ? 0.7 : 1 }}>
                  {explainLoading ? '⟳ Analyzing...' : 'Why did revenue drop?'}
                </button>
              </div>
              {explainLoading && <div style={{ height: 3, background: 'rgba(245,158,11,0.1)', borderRadius: 3, overflow: 'hidden' }}><div style={{ height: '100%', background: '#f59e0b', borderRadius: 3, animation: 'progress 1.8s ease-in-out' }} /></div>}
              {explanation && <div style={{ fontSize: '0.875rem', color: '#a1a1aa', lineHeight: 1.65, background: 'rgba(245,158,11,0.05)', borderRadius: 10, padding: '1rem' }}>{explanation}</div>}
            </div>

            {/* Recent conversations */}
            <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '1.25rem' }}>Recent Conversations</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {bot.logs.slice(0, 6).map(log => (
                  <div key={log.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: '#18181b', borderRadius: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: log.sentiment === 'positive' ? '#10b981' : log.sentiment === 'negative' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.83rem' }}>{log.topic}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: '#71717a' }}>
                      {log.revenue > 0 && <span style={{ color: '#10b981' }}>+${log.revenue}</span>}
                      {log.escalated && <span style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '0.15rem 0.5rem', borderRadius: 4 }}>Escalated</span>}
                      {log.resolved && <span style={{ color: '#10b981' }}>✓ Resolved</span>}
                      <span style={{ color: '#3f3f46' }}>{new Date(log.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ======================== CONFIGURE ======================== */}
        {tab === 'configure' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Section title="Bot Identity">
              <Row label="Bot Name" hint="Appears in the chat header">
                <input style={INPUT} value={bot.name} onChange={e => handleUpdate('name', e.target.value)} placeholder="Aria" onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
              </Row>
              <Row label="Company Name">
                <input style={INPUT} value={bot.companyName} onChange={e => handleUpdate('companyName', e.target.value)} placeholder="Acme Corp" onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
              </Row>
              <Row label="Company Description" hint="2-3 sentences about what the business does">
                <textarea style={TEXTAREA} value={bot.companyDesc} onChange={e => handleUpdate('companyDesc', e.target.value)} placeholder="We are a B2B SaaS platform that..." onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
              </Row>
            </Section>

            <Section title="Personality & Appearance">
              <Row label="Tone & Voice">
                <select style={{ ...INPUT, cursor: 'pointer' }} value={bot.tone} onChange={e => handleUpdate('tone', e.target.value)}>
                  {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
              </Row>
              <Row label="Accent Color" hint="The main color of the chat widget">
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input type="color" value={bot.accentColor} onChange={e => handleUpdate('accentColor', e.target.value)} style={{ width: 44, height: 44, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'none', cursor: 'pointer', padding: 2 }} />
                  <input style={{ ...INPUT, width: 140 }} value={bot.accentColor} onChange={e => handleUpdate('accentColor', e.target.value)} onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                </div>
              </Row>
              <Row label="Widget Position">
                <div style={{ display: 'flex', gap: '0.65rem' }}>
                  {POSITIONS.map(p => (
                    <button key={p} onClick={() => handleUpdate('widgetPosition', p)} style={{ padding: '0.5rem 1.25rem', borderRadius: 8, cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.85rem', background: bot.widgetPosition === p ? 'rgba(245,158,11,0.12)' : '#18181b', border: `1px solid ${bot.widgetPosition === p ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.1)'}`, color: bot.widgetPosition === p ? '#f59e0b' : '#a1a1aa' }}>
                      {p.charAt(0).toUpperCase()+p.slice(1)}
                    </button>
                  ))}
                </div>
              </Row>
            </Section>

            <Section title="Escalation Rules">
              <Row label="Escalation Email">
                <input type="email" style={INPUT} value={bot.escalationEmail} onChange={e => handleUpdate('escalationEmail', e.target.value)} placeholder="support@yourcompany.com" onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
              </Row>
              <Row label="Escalation Keywords" hint="Bot will offer human handoff when these appear in a message">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', padding: '0.65rem', background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                  {bot.escalationKeywords.map((kw, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 100, padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}>
                      {kw}
                      <button onClick={() => handleUpdate('escalationKeywords', bot.escalationKeywords.filter((_,j) => j !== i))} style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.85rem', padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                  <input placeholder="Add keyword + Enter" style={{ border: 'none', background: 'none', outline: 'none', color: '#fafafa', fontFamily: 'Geist, sans-serif', fontSize: '0.8rem', minWidth: 120 }}
                    onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { handleUpdate('escalationKeywords', [...bot.escalationKeywords, e.target.value.trim().toLowerCase()]); e.target.value = ''; }}} />
                </div>
              </Row>
            </Section>

            <Section title="Quick Reply Chips">
              <Row label="Suggested Questions" hint="Shown at the top of the chat — max 4">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {bot.quickReplies.map((qr, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.5rem' }}>
                      <input style={{ ...INPUT, flex: 1 }} value={qr} onChange={e => { const updated = [...bot.quickReplies]; updated[i] = e.target.value; handleUpdate('quickReplies', updated); }} onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                      <button onClick={() => handleUpdate('quickReplies', bot.quickReplies.filter((_,j) => j !== i))} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '0 0.75rem', color: '#ef4444', cursor: 'pointer' }}>×</button>
                    </div>
                  ))}
                  {bot.quickReplies.length < 4 && (
                    <button onClick={() => handleUpdate('quickReplies', [...bot.quickReplies, ''])} style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 8, padding: '0.55rem', color: '#71717a', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.82rem' }}>+ Add quick reply</button>
                  )}
                </div>
              </Row>
            </Section>

            {/* API Key */}
            <Section title="API Key">
              <div style={{ background: '#0a0a0c', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '1.25rem' }}>
                <div style={{ fontSize: '0.78rem', color: '#71717a', marginBottom: '0.75rem' }}>This key identifies your bot. Put it in your widget embed snippet. Never expose it publicly outside the snippet — use the backend proxy for production.</div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ flex: 1, fontFamily: 'Geist Mono, monospace', fontSize: '0.8rem', color: '#f59e0b', background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '0.65rem 0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {keyVisible ? bot.apiKey : bot.apiKey.replace(/(?<=^.{12}).+(?=.{6}$)/g, '•'.repeat(20))}
                  </div>
                  <button onClick={() => setKeyVisible(!keyVisible)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.6rem 0.9rem', color: '#a1a1aa', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{keyVisible ? 'Hide' : 'Show'}</button>
                  <button onClick={copyApiKey} style={{ background: keyCopied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${keyCopied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, padding: '0.6rem 0.9rem', color: keyCopied ? '#10b981' : '#a1a1aa', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{keyCopied ? '✓ Copied' : 'Copy'}</button>
                  <button onClick={() => { if(window.confirm('Regenerate API key? The current key will stop working.')) regenerateApiKey(botId); }} style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '0.6rem 0.9rem', color: '#ef4444', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Regenerate</button>
                </div>
              </div>
            </Section>
          </div>
        )}

        {/* ======================== KNOWLEDGE BASE ======================== */}
        {tab === 'knowledge' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Section title="Knowledge Base Text">
              <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '0.75rem' }}>Paste your FAQs, pricing info, policies, and any other text the bot should know. The more specific and accurate, the better.</div>
              <textarea style={{ ...TEXTAREA, minHeight: 280, width: '100%' }} value={bot.knowledgeBase} onChange={e => handleUpdate('knowledgeBase', e.target.value)} placeholder={`PRICING:\n- Starter: $29/month\n- Pro: $79/month\n\nFAQs:\nQ: How do I cancel?\nA: ...`} onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
            </Section>

            <Section title="Upload Files">
              <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '0.75rem' }}>Upload PDFs, text files, or Word docs. The system extracts text automatically. Max 10MB per file.</div>
              <div onClick={() => fileInputRef.current?.click()} style={{ border: '2px dashed rgba(255,255,255,0.12)', borderRadius: 12, padding: '2.5rem', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.4)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📁</div>
                <div style={{ fontSize: '0.875rem', color: '#a1a1aa', marginBottom: '0.35rem' }}>Click to upload or drag and drop</div>
                <div style={{ fontSize: '0.75rem', color: '#71717a' }}>PDF, TXT, DOCX — up to 10MB each</div>
                <input ref={fileInputRef} type="file" accept=".pdf,.txt,.docx" style={{ display: 'none' }} onChange={handleFileUpload} />
              </div>
              {bot.files && bot.files.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                  {bot.files.map(f => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: '#18181b', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                        <span>📄</span>
                        <div>
                          <div style={{ fontSize: '0.83rem', fontWeight: 500 }}>{f.name}</div>
                          <div style={{ fontSize: '0.72rem', color: '#71717a' }}>{(f.size / 1024).toFixed(1)}KB · Uploaded {new Date(f.uploadedAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.2rem 0.5rem', borderRadius: 4 }}>✓ Indexed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}

        {/* ======================== PLAYBOOKS ======================== */}
        {tab === 'playbooks' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Section title="Active Playbooks">
              <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '1rem' }}>Playbooks define automated actions. When a trigger is met, the bot executes the action — no human needed.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(bot.playbooks || []).map((pb, i) => (
                  <div key={pb.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', background: pb.active ? 'rgba(245,158,11,0.05)' : '#18181b', border: `1px solid ${pb.active ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 10 }}>
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '0.2rem' }}>IF: <span style={{ color: '#f59e0b', fontFamily: 'Geist Mono, monospace' }}>{pb.trigger}</span></div>
                      <div style={{ fontSize: '0.8rem', color: '#71717a' }}>THEN: <span style={{ color: '#10b981' }}>{pb.action}</span></div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: pb.active ? '#10b981' : '#71717a' }}>
                      {pb.active ? '✓ Active' : 'Paused'}
                    </div>
                  </div>
                ))}
                {(bot.playbooks || []).length === 0 && <div style={{ textAlign: 'center', color: '#71717a', padding: '2rem', fontSize: '0.85rem' }}>No playbooks yet. Add one below or use a template.</div>}
              </div>
            </Section>

            <Section title="Quick Templates">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {PLAYBOOK_TEMPLATES.map((t, i) => (
                  <div key={i} onClick={() => addPlaybook(botId, { trigger: t.trigger, action: t.action })} style={{ padding: '1rem', background: '#18181b', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.4rem' }}>{t.icon}</div>
                    <div style={{ fontSize: '0.75rem', color: '#71717a' }}>IF: <span style={{ color: '#f59e0b', fontFamily: 'Geist Mono, monospace', fontSize: '0.72rem' }}>{t.trigger}</span></div>
                    <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: 2 }}>THEN: <span style={{ color: '#10b981', fontSize: '0.72rem' }}>{t.action}</span></div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: '#f59e0b' }}>+ Add →</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Create Custom Playbook">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Row label="Trigger (IF)">
                  <input style={INPUT} value={newPlaybook.trigger} onChange={e => setNewPlaybook({...newPlaybook, trigger: e.target.value})} placeholder='e.g. "sentiment < 3" or "keyword: refund"' onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                </Row>
                <Row label="Action (THEN)">
                  <input style={INPUT} value={newPlaybook.action} onChange={e => setNewPlaybook({...newPlaybook, action: e.target.value})} placeholder='e.g. "Send 10% discount + apology"' onFocus={e => e.target.style.borderColor='rgba(245,158,11,0.5)'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                </Row>
                <button onClick={() => { if(newPlaybook.trigger && newPlaybook.action) { addPlaybook(botId, newPlaybook); setNewPlaybook({ trigger: '', action: '' }); }}} style={{ alignSelf: 'flex-start', background: '#f59e0b', color: '#09090b', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.85rem' }}>
                  Add Playbook
                </button>
              </div>
            </Section>
          </div>
        )}

        {/* ======================== INTEGRATIONS ======================== */}
        {tab === 'integrations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Section title="Available Integrations">
              <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '1rem' }}>Connect your existing tools. When enabled, the bot can read from and write to these services automatically via Playbooks.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {INTEGRATIONS.map(int => (
                  <div key={int.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', background: bot.integrations?.[int.key] ? 'rgba(16,185,129,0.05)' : '#18181b', border: `1px solid ${bot.integrations?.[int.key] ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, transition: 'all 0.15s' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#111113', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', border: '1px solid rgba(255,255,255,0.07)' }}>{int.icon}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{int.label}</div>
                        <div style={{ fontSize: '0.72rem', color: '#71717a', marginTop: 1 }}>{int.desc}</div>
                        <div style={{ fontSize: '0.65rem', color: '#3f3f46', marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{int.category}</div>
                      </div>
                    </div>
                    <div onClick={() => handleUpdate('integrations', { ...bot.integrations, [int.key]: !bot.integrations?.[int.key] })}
                      style={{ width: 44, height: 24, borderRadius: 12, background: bot.integrations?.[int.key] ? '#10b981' : '#27272a', cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: 2, left: bot.integrations?.[int.key] ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fafafa', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* ======================== EMBED ======================== */}
        {tab === 'embed' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Section title="Embed Your Widget">
              <div style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '1rem', lineHeight: 1.6 }}>
                Copy this snippet and paste it before the <code style={{ fontFamily: 'Geist Mono, monospace', background: '#18181b', padding: '0.1rem 0.4rem', borderRadius: 4 }}>&lt;/body&gt;</code> tag on your website. The widget will appear automatically. The API key embedded in the snippet identifies this bot — no other configuration needed.
              </div>
              <div style={{ position: 'relative' }}>
                <pre style={{ background: '#0a0a0c', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1.5rem', fontSize: '0.75rem', fontFamily: 'Geist Mono, monospace', color: '#a1a1aa', overflow: 'auto', lineHeight: 1.7, maxHeight: 420 }}>
                  {generateEmbedSnippet(bot)}
                </pre>
                <button onClick={copyEmbed} style={{ position: 'absolute', top: '1rem', right: '1rem', background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.08)', border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, padding: '0.4rem 0.85rem', color: copied ? '#10b981' : '#a1a1aa', cursor: 'pointer', fontFamily: 'Geist, sans-serif', fontSize: '0.78rem', fontWeight: 600 }}>
                  {copied ? '✓ Copied!' : 'Copy snippet'}
                </button>
              </div>
            </Section>

            <Section title="Installation Guide">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { platform: 'Shopify', steps: 'Go to Online Store → Themes → Edit Code → paste before </body> in theme.liquid' },
                  { platform: 'WordPress', steps: 'Install "Insert Headers and Footers" plugin → Scripts in Footer → paste snippet' },
                  { platform: 'Webflow', steps: 'Project Settings → Custom Code → Footer Code → paste snippet' },
                  { platform: 'Squarespace', steps: 'Settings → Advanced → Code Injection → Footer → paste snippet' },
                  { platform: 'Custom HTML', steps: 'Paste directly before </body> in your HTML file — works on any site' },
                ].map(p => (
                  <div key={p.platform} style={{ padding: '0.9rem 1.25rem', background: '#18181b', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.25rem', color: '#f59e0b' }}>{p.platform}</div>
                    <div style={{ fontSize: '0.8rem', color: '#71717a' }}>{p.steps}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Production Setup">
              <div style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '1.25rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#3b82f6', marginBottom: '0.5rem' }}>ℹ️ For production use</div>
                <p style={{ fontSize: '0.82rem', color: '#71717a', lineHeight: 1.65 }}>
                  The embed snippet above calls our API proxy at <code style={{ fontFamily: 'Geist Mono, monospace', color: '#a1a1aa' }}>api.retflow.ai/v1/chat</code> — your Anthropic key never appears in client-side code. The bot API key in the snippet only identifies which bot configuration to load. Deploy your own backend proxy (see the operator guide) if you want full control.
                </p>
              </div>
            </Section>
          </div>
        )}
      </div>

      <style>{`@keyframes progress { 0% { width: 0% } 100% { width: 100% } }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.5rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '1.25rem', paddingBottom: '0.85rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#a1a1aa', marginBottom: '0.4rem' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '0.72rem', color: '#3f3f46', marginTop: '0.35rem' }}>{hint}</div>}
    </div>
  );
}

function generateEmbedSnippet(bot) {
  return `<!-- RetFlow AI Support Widget | Bot: ${bot.name} -->
<!-- Generated from your RetFlow dashboard — do not edit the api_key -->
<script>
(function() {
  // ============================================================
  // ZENO AI WIDGET — AUTO-GENERATED
  // This snippet is unique to your bot. Paste before </body>.
  // The api_key identifies your bot configuration — it is NOT
  // your Anthropic key. Your Anthropic key stays on the server.
  // ============================================================
  var ZENO_CONFIG = {
    api_key: '${bot.apiKey}',
    // All bot settings (name, tone, colors, knowledge) are loaded
    // from the RetFlow dashboard using this key.
    // To change anything, edit in your dashboard — no code changes needed.
    endpoint: 'https://api.retflow.ai/v1/chat', // Your proxy server
    position: '${bot.widgetPosition}',
    color: '${bot.accentColor}',
  };

  // Load the RetFlow widget script
  var script = document.createElement('script');
  script.src = 'https://cdn.retflow.ai/widget/v1/zeno-widget.min.js';
  script.setAttribute('data-key', ZENO_CONFIG.api_key);
  script.setAttribute('data-position', ZENO_CONFIG.position);
  script.setAttribute('data-color', ZENO_CONFIG.color);
  script.setAttribute('data-endpoint', ZENO_CONFIG.endpoint);
  script.async = true;
  document.head.appendChild(script);
})();
</script>
<!-- End RetFlow AI Widget -->`;
}
